<template>
  <drawer v-bind="$attrs" v-model="modelValue" no-footer width="500">
    <template v-slot:title>
      Notifications
    </template>
    <div class="d-flex ga-2 px-6 py-2 bg-white notifications-filter-bar">
      <qtm-menu-select
        v-model="type"
        clearable
        compact-list
        item-title="label"
        label="Type"
        :items="types"
        @update:model-value="reFetchNotifications"
      />
      <jobsite-menu-select
        v-model="project"
        clearable
        compact-list
        :company="user.company.id"
        @update:model-value="reFetchNotifications"
      />
    </div>
    <template v-for="group in groupedNotifications" :key="group.label">
      <div class="qtm-overline text-mid-grey px-6 pt-4 pb-1 bg-white notification-date-header">
        {{ group.label }}
      </div>
      <notification
        v-for="(item, i) in group.notifications"
        :key="item.id"
        :borderless="i === group.notifications.length - 1"
        :notification="item"
        @click="modelValue = false"
      />
    </template>
    <div
      v-if="!loading && notifications.length === 0"
      class="d-flex flex-column align-center justify-center text-center px-6"
      style="height: 100%"
    >
      <div v-if="type || project">
        <v-icon color="mid-grey" :icon="`${ type ? type.icon : 'mso:notifications' }`" size="48" />
        <p class="qtm-h4 text-mid-grey mt-2" style="max-width: 320px">
          No {{ type ? `${type.label.slice(0, -1)}` : "" }} {{ type?.approval ? "Approval" : "" }} Notifications
          {{ project ? "for This Project" : "" }}
        </p>
      </div>
      <div v-else>
        <v-icon color="mid-grey" icon="mso:notifications" size="48" />
        <p class="qtm-h4 text-mid-grey mt-2">
          New Notifications Will Appear Here
        </p>
        <p class="qtm-body text-mid-grey" style="max-width: 320px">
          Choose how you prefer to be notified in Notification Settings.
        </p>
        <nuxt-link
          class="qtm-label text-interactive text-decoration-none mt-4"
          :to="{ name: 'profile-notifications' }"
          @click="modelValue = false"
        >
          Manage Notifications
        </nuxt-link>
      </div>
    </div>
    <div class="pa-5 text-center">
      <v-progress-circular v-if="loading" color="secondary" indeterminate size="48" width="8" />
      <div v-if="canLoadMore" v-intersect="onIntersect" />
    </div>
  </drawer>
</template>

<script setup lang="ts">
import { DateTime, type Jobsite, type Notification as NotificationData } from '@quotetome/materials-api'
import Drawer from '@/components/drawer.vue'
import JobsiteMenuSelect from '@/components/jobsites/jobsite-menu-select.vue'
import Notification from '@/components/notifications/notification.vue'
import { unreadNotifications } from './notifications-store'

const modelValue = defineModel<boolean>({ default: false })

const canLoadMore = ref(true)
const loading = ref(false)
const notifications = ref<NotificationData[]>([])
const project = ref<Jobsite>()
const type = ref()
const limit = 20
let offset = 0
const { $api, $error } = useNuxtApp()

const authStore = useAuthStore()
const user = authStore.user!
const isApprover = computed(() => (
  user.is_purchase_order_approver
  || user.is_expense_approver
  || user.is_invoice_approver
  || user.is_requisition_approver
))

const types = computed(() => ([
  { type: 'subheader', label: 'APPROVAL REQUESTS', exclude: !isApprover.value },
  {
    label: 'Requisitions',
    approval: true,
    type: 'requisition',
    icon: 'mso:shopping_cart',
    exclude: !isApprover.value
  },
  {
    label: 'Purchase Orders',
    approval: true,
    type: 'order',
    icon: 'mso:package_2',
    exclude: !isApprover.value
  },
  {
    label: 'Expenses',
    approval: true,
    type: 'expense',
    icon: 'mso:receipt_long',
    exclude: !isApprover.value
  },
  {
    label: 'Invoices',
    approval: true,
    type: 'invoice',
    icon: 'mso:attach_money',
    exclude: !isApprover.value
  },
  { type: 'divider', exclude: !isApprover.value },
  { label: 'Comments', type: 'comment', icon: 'mso:comment' },
  { type: 'divider' },
  { type: 'subheader', label: 'UPDATES', exclude: !isApprover.value },
  { label: 'Requisitions', type: 'requisition', icon: 'mso:shopping_cart' },
  { label: 'Quotes', type: 'quote', icon: 'mso:shoppingmode' },
  { label: 'Orders', type: 'order', icon: 'mso:package_2' },
].filter(type => !type.exclude)))

const groupedNotifications = computed(() => {
  const groups: { label: string, notifications: NotificationData[] }[] = []
  const map = new Map<string, NotificationData[]>()

  for (const n of notifications.value) {
    const dt = DateTime.fromUnix(n.date_created)
    const key = dt.format('YYYY-MM-DD')

    if (!map.has(key)) {
      map.set(key, [])
    }

    map.get(key)!.push(n)
  }

  const now = new DateTime()
  const yesterday = now.add(-1, 'day')

  for (const [_key, items] of map) {
    const dt = DateTime.fromUnix(items[0]!.date_created)
    let label: string

    if (dt.isSame(now, 'day')) {
      label = 'TODAY'
    }
    else if (dt.isSame(yesterday, 'day')) {
      label = 'YESTERDAY'
    }
    else {
      const fmt = dt.isSame(now, 'year') ? 'ddd, MMM D' : 'ddd, MMM D, YYYY'
      label = dt.format(fmt).toUpperCase()
    }

    groups.push({ label, notifications: items })
  }

  return groups
})

watch(modelValue, async (open) => {
  if (!open || !unreadNotifications.value) {
    return
  }

  try {
    await $api.v1.notifications.markAsRead()
    unreadNotifications.value = false

    notifications.value
      .filter(n => !n.date_read)
      .forEach(n => { n.date_read = new DateTime() })
  }
  catch (error) {
    $error.report(error)
  }
})

const fetchNotifications = async () => {
  loading.value = true

  try {
    const response = await $api.v1.notifications.list({
      limit,
      offset,
      approval: type.value?.approval != null ? type.value.approval : null,
      project: project.value?.id,
      type: type.value ? type.value.type : null,
    })

    offset += limit

    if (response.data.length < limit) {
      canLoadMore.value = false
    }

    notifications.value = notifications.value.concat(response.data)

    if (notifications.value.length > 0) {
      unreadNotifications.value = Boolean(!notifications.value[0]!.date_read)
    }
  }
  catch (error) {
    $error.report(error)
  }
  finally {
    loading.value = false
  }
}

const reFetchNotifications = async () => {
  notifications.value = []
  offset = 0
  canLoadMore.value = true
  fetchNotifications()
}

const onIntersect = (isIntersecting: boolean) => {
  if (!loading.value && isIntersecting) {
    fetchNotifications()
  }
}

const POLL_INTERVAL = 60_000

let pollTimer: ReturnType<typeof setInterval> | null = null

const startPolling = () => {
  if (pollTimer) {
    return
  }
  pollTimer = setInterval(() => {
    if (!modelValue.value) {
      notifications.value = []
      offset = 0
      canLoadMore.value = true
      fetchNotifications()
    }
  }, POLL_INTERVAL)
}

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

onMounted(() => {
  fetchNotifications()
  startPolling()
})

onUnmounted(() => {
  stopPolling()
})
</script>

<style scoped>
.notifications-filter-bar {
  position: sticky;
  top: 0;
  z-index: 2;
}

.notification-date-header {
  position: sticky;
  top: 57px;
  z-index: 1;
}
</style>
